import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Clear all auth state helper
  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          timeout: 10000
        });
        setUser(response.data);
        setToken(storedToken);
      } catch (error) {
        console.error('Auth init error:', error);
        // Clear invalid/expired token
        clearAuth();
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
  }, [clearAuth]);

  const login = async (email, password) => {
    // Clear any existing stale token before attempting login
    clearAuth();
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { timeout: 10000 });
      const { access_token, user: userData } = response.data;
      
      // Store new token and set state
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      return userData;
    } catch (error) {
      // Ensure auth is cleared on login failure
      clearAuth();
      throw error;
    }
  };

  const register = async (name, email, password) => {
    // Clear any existing stale token before attempting registration
    clearAuth();
    
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password }, { timeout: 10000 });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      return userData;
    } catch (error) {
      clearAuth();
      throw error;
    }
  };

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const getAuthHeaders = useCallback(() => {
    const currentToken = token || localStorage.getItem('token');
    return currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
  }, [token]);

  // Refresh user data (useful after premium upgrade, etc.)
  const refreshUser = useCallback(async () => {
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) return null;
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        timeout: 10000
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Refresh user error:', error);
      clearAuth();
      return null;
    }
  }, [token, clearAuth]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      getAuthHeaders,
      refreshUser,
      clearAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
